import { useEffect, useMemo, useRef, useState } from 'react';
import type { CertificatePlaceholder } from '@mlm/shared';
import { Button } from './button';
import { Card, CardContent, CardHeader } from './card';
import { ConfirmDialog } from './confirm-dialog';
import { Input } from './input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import {
  availableFields,
  certificateFieldLabel,
  certificateFieldSample,
  certificateFont,
  CERTIFICATE_REFERENCE_WIDTH,
  clampPct,
  defaultPlaceholder,
  fitFontSize,
  nudgePlaceholder,
  type CertificateTemplateEditorProps,
} from '../lib/certificate-layout';

/**
 * The certificate template editor: a background image with draggable text
 * placeholders on it, plus a numeric panel for exact values.
 *
 * Shared by the creator portal and the admin house-template page, which is the
 * whole point — a house template and a creator's template are the same row in
 * the same table, and two hand-maintained editors would drift.
 *
 * Three things it gets right that the creator's original did not: the preview
 * declares `container-type: inline-size`, so the `cqw` font maths it already
 * used is actually true; text shrinks to `widthPct` exactly as the issued
 * canvas does; and dragging is POINTER-based with keyboard nudging, so it works
 * on the tablet a creator is most likely holding.
 */
export function CertificateTemplateEditor({
  templateId,
  name: initialName,
  imageUrl,
  placeholders: initialPlaceholders,
  saving = false,
  deleting = false,
  maxPlaceholders = 4,
  allowImageReplace = true,
  deleteDescription = 'Courses using it will stop issuing certificates. Already-issued certificates are unaffected.',
  onSave,
  onDelete,
}: CertificateTemplateEditorProps) {
  const [name, setName] = useState(initialName);
  const [placeholders, setPlaceholders] = useState<CertificatePlaceholder[]>(initialPlaceholders);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [replacementUrl, setReplacementUrl] = useState<string | null>(null);
  const [active, setActive] = useState(0);
  const [confirm, setConfirm] = useState(false);
  const [fontsReady, setFontsReady] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const dragging = useRef(false);

  // A different template means a different draft. Keyed on the id, not the
  // object, so a refetch that returns an equal row does not discard edits.
  useEffect(() => {
    setName(initialName);
    setPlaceholders(initialPlaceholders);
    setImageFile(null);
    setActive(0);
    if (fileRef.current) fileRef.current.value = '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  // Measuring against the fallback font places every field slightly wrong on a
  // cold cache. Wait for the real face, but never block on it: a WebView that
  // never resolves `fonts.load` must still render an editable preview.
  useEffect(() => {
    let alive = true;
    const done = () => alive && setFontsReady(true);
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    if (!fonts?.load) {
      done();
      return;
    }
    Promise.race([fonts.load(certificateFont(48)), new Promise((r) => setTimeout(r, 1500))]).then(
      done,
      done,
    );
    return () => {
      alive = false;
    };
  }, []);

  // A locally chosen replacement previews immediately, so the creator places
  // fields on the image they are about to save, not the one being replaced.
  useEffect(() => {
    if (!imageFile) {
      setReplacementUrl(null);
      return;
    }
    const url = URL.createObjectURL(imageFile);
    setReplacementUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const measure = useMemo(() => {
    const ctx =
      typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');
    return (font: string, text: string) => {
      if (!ctx) return 0;
      ctx.font = font;
      return ctx.measureText(text).width;
    };
  }, []);

  const cur = placeholders[active];
  const patch = (i: number, p: Partial<CertificatePlaceholder>) =>
    setPlaceholders((ps) => ps.map((x, j) => (j === i ? { ...x, ...p } : x)));

  function pointToPct(e: { clientX: number; clientY: number }) {
    const el = previewRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return {
      xPct: clampPct(((e.clientX - r.left) / r.width) * 100),
      yPct: clampPct(((e.clientY - r.top) / r.height) * 100),
    };
  }

  /**
   * The rendered size of a placeholder, in reference-design pixels — the same
   * number the issued canvas computes, so the preview can scale it with `cqw`.
   */
  function fittedSize(p: CertificatePlaceholder): number {
    if (!fontsReady) return p.fontSizePx;
    const maxW = (p.widthPct / 100) * CERTIFICATE_REFERENCE_WIDTH;
    return fitFontSize(measure, certificateFieldSample(p.field), p.fontSizePx, maxW);
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="max-w-xs font-medium"
          aria-label="Template name"
        />
        <div className="flex items-center gap-2">
          {allowImageReplace && (
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
              {imageFile ? 'Image chosen' : 'Replace image'}
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setConfirm(true)} disabled={deleting}>
            Delete
          </Button>
          <Button
            size="sm"
            onClick={() => onSave({ name, config: { placeholders }, imageFile })}
            disabled={saving || !name}
          >
            {saving ? 'Saving…' : 'Save layout'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg"
          className="hidden"
          onChange={(e) => setImageFile(e.target.files?.[0] ?? null)}
        />
        <div
          ref={previewRef}
          className="relative w-full select-none overflow-hidden rounded border bg-muted"
          // `cqw` below is measured against THIS box, which only works if the
          // box is a size container. Without it the browser falls back to the
          // viewport and every preview font is wrong.
          style={{ containerType: 'inline-size', touchAction: 'none' }}
          onPointerMove={(e) => {
            if (!dragging.current || !cur) return;
            const p = pointToPct(e);
            if (p) patch(active, p);
          }}
          onPointerUp={() => (dragging.current = false)}
          onPointerCancel={() => (dragging.current = false)}
        >
          <img
            src={replacementUrl ?? imageUrl}
            alt=""
            className="block h-auto w-full"
            draggable={false}
          />
          {placeholders.map((p, i) => (
            <div
              key={i}
              role="button"
              tabIndex={0}
              aria-label={`${certificateFieldLabel(p.field)} placeholder`}
              onPointerDown={(e) => {
                e.preventDefault();
                (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
                setActive(i);
                dragging.current = true;
              }}
              onPointerUp={(e) => {
                (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
                dragging.current = false;
              }}
              onKeyDown={(e) => {
                if (
                  e.key !== 'ArrowLeft' &&
                  e.key !== 'ArrowRight' &&
                  e.key !== 'ArrowUp' &&
                  e.key !== 'ArrowDown'
                ) {
                  return;
                }
                e.preventDefault();
                setActive(i);
                setPlaceholders((ps) =>
                  ps.map((x, j) =>
                    j === i ? nudgePlaceholder(x, e.key as 'ArrowLeft', e.shiftKey ? 2 : 0.5) : x,
                  ),
                );
              }}
              className={`absolute -translate-y-1/2 cursor-move whitespace-nowrap rounded px-1 outline-none focus-visible:ring-2 focus-visible:ring-ring ${i === active ? 'ring-2 ring-primary' : 'ring-1 ring-primary/40'}`}
              style={{
                left: `${p.xPct}%`,
                top: `${p.yPct}%`,
                transform: `translate(${p.align === 'center' ? '-50%' : p.align === 'right' ? '-100%' : '0'}, -50%)`,
                color: p.color,
                fontSize: `calc(${fittedSize(p)}px * (100cqw / ${CERTIFICATE_REFERENCE_WIDTH}px))`,
                fontWeight: 600,
                fontFamily: 'var(--font-certificate, "Hanken Grotesk", system-ui, sans-serif)',
              }}
              title={certificateFieldLabel(p.field)}
            >
              {certificateFieldSample(p.field)}
            </div>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground">
          Drag a field to move it, or focus one and use the arrow keys (hold Shift for bigger
          steps). Font sizes are relative to a {CERTIFICATE_REFERENCE_WIDTH}px-wide design and
          shrink to fit the field&rsquo;s max width, exactly as the issued certificate does.
        </p>

        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {placeholders.map((p, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`rounded border px-2 py-1 text-xs ${i === active ? 'bg-accent font-medium' : ''}`}
              >
                {certificateFieldLabel(p.field)}
              </button>
            ))}
            {placeholders.length < maxPlaceholders && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const next = defaultPlaceholder(placeholders);
                  if (!next) return;
                  setPlaceholders((ps) => [...ps, next]);
                  setActive(placeholders.length);
                }}
              >
                Add field
              </Button>
            )}
          </div>
          {cur && (
            <div className="grid grid-cols-2 gap-3 rounded border p-3 sm:grid-cols-4">
              <LabelledField label="Field">
                <Select
                  value={cur.field}
                  onValueChange={(v) =>
                    patch(active, { field: v as CertificatePlaceholder['field'] })
                  }
                >
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableFields(placeholders, active).map((f) => (
                      <SelectItem key={f.value} value={f.value}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </LabelledField>
              <LabelledField label="Align">
                <Select
                  value={cur.align}
                  onValueChange={(v) =>
                    patch(active, { align: v as CertificatePlaceholder['align'] })
                  }
                >
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left">Left</SelectItem>
                    <SelectItem value="center">Center</SelectItem>
                    <SelectItem value="right">Right</SelectItem>
                  </SelectContent>
                </Select>
              </LabelledField>
              <LabelledField label="Font size">
                <Input
                  type="number"
                  min={8}
                  max={200}
                  value={cur.fontSizePx}
                  onChange={(e) => patch(active, { fontSizePx: Number(e.target.value) || 12 })}
                  className="h-8"
                />
              </LabelledField>
              <LabelledField label="Color">
                <Input
                  type="color"
                  value={cur.color}
                  onChange={(e) => patch(active, { color: e.target.value })}
                  className="h-8 p-1"
                />
              </LabelledField>
              <LabelledField label="X %">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={Math.round(cur.xPct)}
                  onChange={(e) => patch(active, { xPct: clampPct(Number(e.target.value)) })}
                  className="h-8"
                />
              </LabelledField>
              <LabelledField label="Y %">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={Math.round(cur.yPct)}
                  onChange={(e) => patch(active, { yPct: clampPct(Number(e.target.value)) })}
                  className="h-8"
                />
              </LabelledField>
              <LabelledField label="Max width %">
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={cur.widthPct}
                  onChange={(e) => patch(active, { widthPct: Number(e.target.value) || 10 })}
                  className="h-8"
                />
              </LabelledField>
              <div className="flex items-end">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setPlaceholders((ps) => ps.filter((_, i) => i !== active));
                    setActive(0);
                  }}
                >
                  Remove field
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title="Delete this template?"
        description={deleteDescription}
        confirmLabel="Delete"
        confirmVariant="destructive"
        onConfirm={onDelete}
      />
    </Card>
  );
}

function LabelledField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}
